// 🌐 Selector de Idioma
import { Check, Languages } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { useUserPreferences } from '@/lib/hooks/useUserPreferences';
import { languages, idiomaPorDefecto, type SupportedLanguages } from '@/lib/i18n';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function LanguageSelector() {
  const { currentLanguage, changeLanguage, t } = useTranslation();
  const { updatePreferences } = useUserPreferences();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Normalizar el idioma actual a uno de los soportados
  const normalizedLanguage = (currentLanguage?.split('-')[0] || idiomaPorDefecto) as SupportedLanguages;
  const safeLanguage = languages[normalizedLanguage] ? normalizedLanguage : idiomaPorDefecto;

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = (lng: SupportedLanguages) => {
    changeLanguage(lng);
    setIsOpen(false);

    // Sincronizar con backend usando el hook
    updatePreferences({ language: lng });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        <Languages className="w-4 h-4" />
        <span className="hidden sm:inline">{languages[safeLanguage].flag}</span>
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-48 rounded-lg border border-border bg-popover p-1 shadow-lg z-50"
          >
            <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
              {t('settings.selectLanguage')}
            </div>
            <div className="space-y-0.5">
              {(Object.keys(languages) as SupportedLanguages[]).map((lng) => (
                <button
                  key={lng}
                  onClick={() => handleLanguageChange(lng)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm
                    transition-colors duration-150
                    ${
                      safeLanguage === lng
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <span className="text-xl">{languages[lng].flag}</span>
                  <span className="flex-1 text-left">{languages[lng].name}</span>
                  {safeLanguage === lng && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
